import {CognitoUser} from 'amazon-cognito-identity-js'
import {Auth} from 'aws-amplify'
import axios from 'axios'
import {
  setSessionAnchorNow,
  syncSessionPreferenceFromCognito,
} from '../../../../shared/utils/sessionExpirationPreference'
import {CustomCognitoUser, mapCognitoAttributesToUserAttributes, UserAttributes, UserModel} from './_models'
import {apiCallWithAuthCheck} from './AuthErrorInterceptor'

const API_URL = process.env.REACT_APP_API_URL

export const GET_USER_BY_ACCESSTOKEN_URL = `${API_URL}/verify_token`
export const LOGIN_URL = `${API_URL}/login`
export const REGISTER_URL = `${API_URL}/register`
export const REQUEST_PASSWORD_URL = `${API_URL}/forgot_password`
export const AUTH_USER_TOKEN_KEY = `JWT-TOKEN`

// Server should return CognitoUser
// https://github.com/aws-amplify/amplify-js/blob/4644b4322ee260165dd756ca9faeb235445000e3/packages/amazon-cognito-identity-js/index.d.ts#L48
export async function cognitologin(email: string, password: string): Promise<CognitoUser> {
  return Auth.signIn(email, password, {})
}

export type SignInMfaType = 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA'

export async function confirmCognitoMfa(
  user: CognitoUser,
  code: string,
  mfaType: SignInMfaType
): Promise<CognitoUser> {
  return Auth.confirmSignIn(user, code, mfaType)
}

/**
 * Answers SELECT_MFA_TYPE (both SMS and an authenticator app enabled, none preferred) by picking
 * the authenticator app, which does not depend on SMS delivery. Amplify v5's Auth has no wrapper
 * for this, so it calls the Cognito user directly. Resolves with the challenge Cognito asks next.
 */
export function selectCognitoTotpMfa(user: CognitoUser): Promise<SignInMfaType> {
  return new Promise((resolve, reject) => {
    user.sendMFASelectionAnswer('SOFTWARE_TOKEN_MFA', {
      totpRequired: () => resolve('SOFTWARE_TOKEN_MFA'),
      mfaRequired: () => resolve('SMS_MFA'),
      onSuccess: () => reject(new Error('Unexpected sign-in result after choosing an MFA method')),
      onFailure: reject,
    })
  })
}

export async function finalizeCognitoLogin(
  user: CognitoUser,
  setCurrentUser: (attrs: UserAttributes) => void,
  saveCognitoAuth: (auth: CustomCognitoUser | undefined) => void
): Promise<void> {
  const attrs = await mapCognitoAttributesToUserAttributes(user)
  syncSessionPreferenceFromCognito(attrs.sessionExpirationHours)
  setCurrentUser(attrs)
  saveCognitoAuth(user)
  setSessionAnchorNow()
}

export async function cognitoSignUp(
  email: string,
  firstname: string,
  lastname: string,
  password: string
): Promise<CognitoUser> {
  const {user} = await Auth.signUp({
    username: email,
    password,
    attributes: {
      email,
      given_name: firstname,
      family_name: lastname,
    },
  })
  return user
}

export async function confirmCognitoSignUp(userName: string, code: string): Promise<void> {
  await Auth.confirmSignUp(userName, code)
}

export async function resendCognitoSignUp(userName: string): Promise<void> {
  await Auth.resendSignUp(userName)
}

// Server should return object => { result: boolean } (Is Email in DB)
export function requestPassword(email: string): Promise<any> {
  return Auth.forgotPassword(email, {})
}

export function getUserByToken(token: string) {
  return apiCallWithAuthCheck(async () => {
    return axios.post<UserModel>(GET_USER_BY_ACCESSTOKEN_URL, {
      api_token: token,
    })
  })
}
